import BaseConfiguratorService from '@configuratorware/configurator-frontendgui/App/Services/ConfiguratorService';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { isEqual, omit } from 'lodash';
import { Steps } from '../Reducers/App/Selectors';
import Api from '@configuratorware/configurator-frontendgui/Framework/Api';
import { acquireIdToken } from '../authConfig';
import { setAccessDeniedError, setCurrentStep, setUserFlags } from '../Reducers/App/Actions';

// Identifier stashed here when a configuration-load request is access-denied.
// Consumed (read + cleared) by LoadConfigurationContainer to show the inline error.
let _loadConfigAccessDeniedIdentifier = null;

export const consumeLoadConfigAccessDeniedIdentifier = () => {
  const id = _loadConfigAccessDeniedIdentifier;
  _loadConfigAccessDeniedIdentifier = null;
  return id;
};

const isConfigurationLoadRequest = (error) =>
  (error?.config?.url ?? '').startsWith('configuration/loadbyconfigurationcode/');

const SESSION_EXPIRED = 'SESSION_EXPIRED';

const isAuthFailure = (error) => [401, 403].includes(error?.response?.status);

// A denial the application itself decided on: ErrorEventSubscriber renders those with `type` and
// `identifier`. A 401/403 without them came from the firewall, which means the token was rejected
// rather than the user's permissions.
const isApplicationDenial = (error) => error?.response?.data?.type === 'access_denied';

/*
 * The bundle's `asyncRequest` passes `error.response.data` straight to the receiving action
 * whenever the body is an object, so a rejected request gets reduced as if it were a
 * configuration — which is how an expired session turns into a broken screen instead of a message.
 * Dropping `response` keeps the failure on the error path, where the callers already report it.
 */
const withoutResponseBody = (error) => {
  const sanitized = new Error(error?.message ?? 'Request failed');
  sanitized.isAuthFailure = true;
  sanitized.status = error?.response?.status;
  sanitized.config = error?.config;
  return sanitized;
};

const setupCustomRequestHandler = async () => {
  const request = Api.request;

  const send = async (args, forceRefresh) => {
    const authToken = await acquireIdToken(forceRefresh);
    Api.setHeader('Authorization', authToken);
    return request.call(Api, ...args);
  };

  const report = (error) => {
    if (isConfigurationLoadRequest(error)) {
      // Show inline error in the load-configuration overlay — no redirect or dialog
      _loadConfigAccessDeniedIdentifier = isApplicationDenial(error)
        ? error?.response?.data?.identifier ?? 'NO_MACHINE_TYPE_ACCESS'
        : SESSION_EXPIRED;
      return;
    }
    if (isApplicationDenial(error)) {
      // Redirect first, then show dialog (per fit criteria)
      Services.store.dispatch(setCurrentStep(Steps.START));
      Services.store.dispatch(setAccessDeniedError(error.response.data.identifier));
      return;
    }
    Services.store.dispatch(setAccessDeniedError(SESSION_EXPIRED));
  };

  Api.request = async (...args) => {
    try {
      return await send(args, false);
    } catch (error) {
      if (!isAuthFailure(error)) throw error;

      /*
       * The firewall rejected the token itself, so MSAL may still be holding a stale one. Renew it
       * and give the request one more attempt. Retrying is safe even for writes: a request refused
       * at the firewall never reached application code, so nothing was persisted.
       */
      if (!isApplicationDenial(error)) {
        try {
          return await send(args, true);
        } catch (retryError) {
          if (!isAuthFailure(retryError)) throw retryError;
          report(retryError);
          throw withoutResponseBody(retryError);
        }
      }

      report(error);
      throw withoutResponseBody(error);
    }
  };
};

const EQUAL_CHECK_TYPE_CALCULATION = 'calculation';

class ConfiguratorService extends BaseConfiguratorService {
  constructor() {
    super();

    Services.store.observeStore(
      (state) => state.ipco.currentStep,
      (currentStep) =>
        currentStep === Steps.CONFIGURATOR &&
        Services.visualization.update(Services.store.state.configurator.configuration)
    );

    setupCustomRequestHandler().then(() =>
      Api.request({ url: 'user/flags' })
        .then((result) => Services.store.dispatch(setUserFlags(result.data)))
        .catch((e) => console.error('Failed to load user flags', e))
    );
  }

  prepareConfigurationForSave(...args) {
    const configuration = super.prepareConfigurationForSave(...args);
    const { customdata } = configuration;
    const {
      currentStep,
      selectedSystem,
      selectedProductType,
      systemParameters,
      productParamsValidation,
      configuratorMenuOptions,
      selectedConfiguratorMenu,
    } = Services.store.state.ipco;

    return {
      ...configuration,
      customdata: {
        ...customdata,
        appState: {
          currentStep,
          selectedSystem,
          selectedProductType,
          systemParameters,
          productParamsValidation,
          configuratorMenuOptions,
          selectedConfiguratorMenu,
        },
      },
    };
  }

  shouldInvalidateCalculation(prevConfiguration, configuration) {
    return this.isRoughEqual(configuration, prevConfiguration, EQUAL_CHECK_TYPE_CALCULATION);
  }

  isRoughEqual(configuration, prevConfiguration, target) {
    const pc = this.getRoughConfiguration(prevConfiguration, target);
    const cc = this.getRoughConfiguration(configuration, target);
    return !isEqual(cc, pc);
  }

  getRoughConfiguration(configuration, target) {
    const roughConfiguration = super.getRoughConfiguration(configuration);
    if (target === EQUAL_CHECK_TYPE_CALCULATION)
      roughConfiguration.customdata = omit(roughConfiguration.customdata, [
        'surrounding_height',
        'surrounding_length',
        'surrounding_width',
        'surrounding_type',
      ]);
    return roughConfiguration;
  }

  shouldUpdateVisualization(prevConfiguration, configuration) {
    return (
      Services.store.state.ipco.currentStep === Steps.CONFIGURATOR &&
      super.shouldUpdateVisualization(prevConfiguration, configuration)
    );
  }
}

export default ConfiguratorService;
